import { RosTemplateType } from "@/services";

export const HOME_CARE_ROS_OPTIONS: Record<string, string> = {
  General:
    "WNL,Lost_Weight,Gain_Weight,Night_Sweats,Ataxia,Fatigue,Obese,Anorexia,Cachectic,Limited_Ambulation,Fever,Chills,Alert,Awake,Lethargic,Obtunded,Stupor,Comatose,Confused,Allergy:freeText,Immobile,Others:freeText",
  Head: "WNL,Trauma,Vertigo,Syncope,Dizziness,Headache,Masses,Contusion,Abrasion,Others:freeText",
  Eyes: "WNL,Blurring,Diplopia,Involuntary_Blinking,w/_Glasses,Erythema,Arcus_Senilis,PERRLA,Dry_Eyes,Strabismus,Conjunctiva_Discharge,Color:freeText,Color_R,Color_L,Blind_R,Blind_L,Others:freeText",
  Ears: "WNL,HOH,Deaf,Impacted_Cerumen,Ear_Pain,w/External_Hearing_Aid,Tinnitus,Others:freeText",
  Nose: "WNL,Epistaxis,Congestion,Rhinorrhea,Others:freeText",
  Mouth:
    "WNL,Sores,Gingival_Bleeding,Sticking_Out_Tongue,w/Dentures,Dysphasia,Dry_Mouth,Sore_Throat,Dysphagia,Mucosa_Dry,Mucosa_Redness,Discharge,Color:freeText,Others:freeText",
  Cardiovascular:
    "WNL,Arrhythmia,Known_Murmur,Chest_Pain,Bradycardia,Tachycardia,Pacemaker,Palpitation,CABG,Syncope,Orthopnea,CAD,Others:freeText",
  Pulmonary:
    "WNL,Dyspnea,At_Rest,Orthopnea,Cough,Hemoptysis,Wheezes,Rhonchi,Rales,Crackles,Location_R,Location_L,Others:freeText",
  Abdomen:
    "WNL,Nausea,Heartburn,Vomiting,Tenderness_RLQ,Tenderness_RUQ,Tenderness_LLQ,Tenderness_LUQ,Pain,Hard,Constipation,Non-Tender,Masses_Location:freeText,Distended,Soft,Last_Bowel_Movement:freeText,Others:freeText",
  Genitourinary:
    "WNL,Dysuria,Hematuria,Increased_Frequency,Foul_Odor,Incontinent,w/Catheter,Cloudy_Urine,Others:freeText",
  Rectal:
    "WNL,Hemorrhoids,Rash,Redness,Discharge,Bleeding,Wearing_Diaper,Others:freeText",
  "Upper Extremities":
    "WNL,Limited_Movement,Weakness_R,Weakness_L,Numbness_And_Tingling_R,Numbness_And_Tingling_L,Radial_Pulse_Weak,Radial_Pulse_Absent,Radial_Pulse_R,Radial_Pulse_L,Shaking,Edema_Pitting,Edema_Non-Pitting:freeText,Non-Pitting_R,Non-Pitting_L,Others:freeText",
  "Lower Extremities":
    "WNL,Limited_Movement,Weakness_R,Weakness_L,Numbness_And_Tingling_R,Numbness_And_Tingling_L,Radial_Pulse_Weak,Radial_Pulse_Absent,Radial_Pulse_R,Radial_Pulse_L,Shaking,Edema_Pitting,Edema_Non-Pitting:freeText,Non-Pitting_R,Non-Pitting_L,Others:freeText",
  Skin: "WNL,Ecchymosis,Ulcers,Pruritus,Wound,Laceration,Cellulitis,Jaundice,Rash,Papules_Location:freeText,Decreased_Turgor,Pressure_Ulcers_Location:freeText,Wound_Stage:freeText,Others:freeText",
  Nutrition:
    "WNL,Regular_Diet,Mechanical_Soft,Puree_Diet,Thickened_Liquid,Poor_Appetite,Low_Sodium,Low_Fat/Low_Cholesterol_Diet,Renal_Diet,Others:freeText",
  Neurological:
    "WNL,Facial_Weakness,Numbness,Dizziness,Seizure,Tremors,Slurred_Speech,Hand_Grip_Weak_R,Hand_Grip_Weak_L,Paralysis_R,Paralysis_L,Mild_Cognitive_Delay/Learning_Difficulties,Half_Body_Weakness_R,Half_Body_Weakness_L,Facial_Drooping_R,Facial_Drooping_L,Stuttering,Non-Verbal,Others:freeText",
  Mental:
    "WNL,Liability_Of_Mood,Hallucinations,Delusions,Depression,Somnolence,Insomnia,Anxious,Disoriented,Forgetful,Lethargic,Confused,Hearing_Voices,Others:freeText",
};

export const HOME_CARE_ROS_SAFETY_ASSESSMENT = "Safety Assessment";

export interface SafetyQuestion {
  key: string;
  label: string;
  type: "multi" | "single" | "text";
  options?: string[];
  // Ticking this option clears the others, and ticking any other clears it.
  exclusiveOption?: string;
  followUps?: SafetyQuestion[];
}

const YES_NO = ["Yes", "No"];

export const HOME_CARE_ROS_SAFETY_QUESTIONS: SafetyQuestion[] = [
  {
    key: "Assistive_Device",
    label: "Does patient use an assistive device?",
    type: "multi",
    options: ["Cane", "Walker", "Crutches", "Wheelchair", "Bedbound"],
  },
  {
    key: "Difficulty_Ambulating",
    label: "Does patient have difficulties ambulating within their home?",
    type: "single",
    options: YES_NO,
  },
  {
    key: "Obstacles",
    label: "Are there obstacles that make ambulating difficult?",
    type: "multi",
    options: ["None", "Stairs", "Clutter", "Slippery_Surfaces"],
    exclusiveOption: "None",
  },
  {
    key: "Assistance_In_Home",
    label: "Does patient have assistance in home?",
    type: "single",
    options: YES_NO,
    followUps: [
      { key: "Assistance_Who", label: "If yes, who?", type: "text" },
      { key: "Assistance_Relationship", label: "Relationship", type: "text" },
    ],
  },
  {
    key: "Elderly_Abuse_Concern",
    label: "Is there any concern or evidence of elderly abuse?",
    type: "single",
    options: YES_NO,
    followUps: [
      {
        key: "Elderly_Abuse_Signs",
        label: "If yes, signs of elderly abuse",
        type: "text",
      },
    ],
  },
  {
    key: "Fall_Risk",
    label: "Is patient a fall risk?",
    type: "single",
    options: YES_NO,
    followUps: [
      {
        key: "Falls_Last_Year",
        label: "If yes, how many times in last year?",
        type: "single",
        options: ["1", "2", "3", "4", "5"],
      },
    ],
  },
];

export const HOME_CARE_ROS_INITIAL_DATA: RosTemplateType[] = [
  ...Object.keys(HOME_CARE_ROS_OPTIONS),
  HOME_CARE_ROS_SAFETY_ASSESSMENT,
].map((name) => ({ name, value: "", isSelected: true, isBaseType: true }));
